import { SportType } from '../../court/dto/court.dto';

export const MIN_LEVEL = 1;
export const MAX_LEVEL = 10;

// Canonical gender vocabulary (normalized server-side from whatever a bulk
// import source exported). Absent/unknown = null, not a member.
export const USER_GENDERS = ['male', 'female'] as const;
export type UserGender = (typeof USER_GENDERS)[number];

// Full extraction + verdict trail produced by the document-verification cron.
// Stored as-is on user.signupDocumentVerificationResult so the dashboard can
// show a side-by-side comparison with the profile and an audit trail of why
// the verdict was reached.
export type SignupDocumentStatus =
  | 'pending'
  | 'verified'
  | 'rejected'
  | 'needs_review';

export type SignupDocumentVerificationResult = {
  /** Raw GPT extraction. Best-effort, fields may be missing if Vision couldn't read them. */
  extracted: {
    firstName?: string | null;
    lastName?: string | null;
    documentType?: string | null;
    expiryDate?: string | null;
    institution?: string | null;
    uniqueId?: string | null;
    isGenuine?: boolean | null;
    genuineConfidence?: number | null;
    isPhotoOfScreen?: boolean | null;
  };
  /** Per-name match confidences (0..1) against the user profile. */
  match: {
    firstNameConfidence: number;
    lastNameConfidence: number;
  };
  /** Final verdict written by determineVerdict(). */
  verdict: 'verified' | 'rejected' | 'needs_review';
  /** Short machine-readable code explaining the verdict for the dashboard. */
  reason: string;
  /** ID of the auto-assigned UserMembership, if any. null when no assignment occurred. */
  assignedMembershipId?: string | null;
  /** ISO timestamp of when this result was produced. */
  evaluatedAt: string;
  /** Admin who acted on the document (admin_approved / admin_rejected paths). null for cron-produced verdicts. */
  actor?: { adminEmail: string } | null;
};

export type SportLevelDto = {
  sportType: SportType;
  level: number;
  identificationNumber?: string;
  officialRanking?: string;
  /** Admin has verified the user's level in this sport. */
  isLevelVerified?: boolean;
  /** Admin has verified the identification number / official ranking (license) for this sport. */
  isIdentificationVerified?: boolean;
};

export type UserDto = {
  id: string;
  firstName: string;
  lastName: string;
  level: number;
  avatar?: string;
  email: string;
  isArchived?: boolean;
  // Account-less player (a host paid for them, a walk-in): no login, no push.
  isGuest?: boolean;
  /** Active membership type names, newest first; the org default alone when
   *  isDefaultMembership. Populated only by GET users (admin view). */
  membershipTypeNames?: string[];
  /** True when membershipTypeNames is the org default fallback. Admin views only. */
  isDefaultMembership?: boolean;
  sportLevels?: SportLevelDto[];
  lastActiveAt?: string;
  /** When false, user cannot book courts. Default true. */
  canBookCourts?: boolean;
  /** When false, user cannot use Pay Later. Default true. */
  canUsePayLater?: boolean;
  /** When false, user is hidden from player discovery suggestions. Default true. */
  discoverable?: boolean;
  /** True when the row came from a bulk client import. Admin views only. */
  createdFromExport?: boolean;
  /** ISO date of when an imported client first signed in (adopted their row).
   *  null = imported but not yet signed in. Admin views only. */
  claimedAt?: string | null;
  /** Populated only in admin views. null = no welcome email sent yet. */
  emailDeliveryStatus?:
    | 'pending'
    | 'delivered'
    | 'opened'
    | 'bounced'
    | 'failed'
    | null;
  /**
   * The raw blob path is stripped from these responses. The boolean below
   * tells the dashboard whether a document exists; the SAS URL lives on
   * /users/:userId/signup-document (admin only).
   */
  hasSignupDocument?: boolean;
  // 4-value verdict (no PII) — exposed on /users/:id/details. Extracted PII
  // (uniqueId, institution, actor) is admin-only and lives on the
  // /users/:userId/signup-document response (see SignupDocumentResponse in
  // useGetSignupDocumentUrl), NOT here.
  signupDocumentStatus?: SignupDocumentStatus | null;
  notificationPreferences?: {
    gameActivity: boolean;
    invitations: boolean;
    eventsAndAlerts: boolean;
    promotions: boolean;
  };
  contactInfo?: {
    phoneNumber?: string;
    postalCode?: string;
  };
  /** Canonical gender. null/absent = never provided. Admin/self views only. */
  gender?: UserGender | null;
};
export type UserFullProfileDto = UserDto & {
  question1Answer?: number;
  question2Answer?: number;
  question3Answer?: number;
  question4Answer?: number;
  question5Answer?: number;
  question6Answer?: number;
  question7Answer?: number;
  question8Answer?: number;
  question9Answer?: number;
  question10Answer?: number;
};
